import React from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash/merge';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import InlineContactRelationForm from './inline_contact_relation_form';
import contactRolePropTypes from 'bundles/project/prop_types/contact_roles';
import { pluralize } from 'helpers/formatters/string_formats';
import CopyContactRoleModalBase from 'components/contacts/copy_contact_role_modal';

export default class InlineContactSelector extends React.Component {
  static propTypes = {
    availableDatabaseOptions: PropTypes.array.isRequired,
    contactRoles: contactRolePropTypes.isRequired,
    contacts: PropTypes.arrayOf(PropTypes.shape({
      _destroy: PropTypes.bool,
      id: PropTypes.number,
      contactId: PropTypes.number,
      contactType: PropTypes.string,
      contactName: PropTypes.string,
      relationship: PropTypes.string,
      durationStart: PropTypes.string,
      durationEnd: PropTypes.string
    })).isRequired,
    databaseContactRoles: PropTypes.arrayOf(PropTypes.shape({
      contact: PropTypes.shape({
        email: PropTypes.string,
        name: PropTypes.string,
        phoneFormatted: PropTypes.string,
        referralSourceLabel: PropTypes.string
      }).isRequired,
      id: PropTypes.number,
      durationStart: PropTypes.string,
      durationEnd: PropTypes.string,
      relationshipLabel: PropTypes.string
    })),
    onContactsUpdate: PropTypes.func.isRequired
  };

  static defaultProps = {
    databaseContactRoles: []
  };

  state = {
    showContactCopyModal: false
  };

  showContactCopyModal = (e) => {
    e.preventDefault();
    this.setState({ showContactCopyModal: true });
  }

  hideContactCopyModal = () => {
    this.setState({ showContactCopyModal: false });
  }

  listContactRolesToAdd = () => {
    return this.props.databaseContactRoles.filter((contactRole) => {
      const selectedContactIds = this.props.contacts.map(contact => contact.contactId);

      return !selectedContactIds.includes(contactRole.contact.id);
    });
  }

  copyFromContactRoles = (contactRoles) => {
    const contactList = contactRoles.map((contactRole) => {
      return {
        contactId: contactRole.contact.id,
        contactType: contactRole.contact.contactType,
        contactName: contactRole.contact.name,
        durationEnd: contactRole.durationEnd,
        durationStart: contactRole.durationStart,
        relationship: contactRole.relationship
      };
    });
    this.updateContacts(contacts => contacts.push(...contactList));
    this.hideContactCopyModal();
  }

  addContact = (e) => {
    e.preventDefault();
    this.updateContacts(contacts => contacts.push({}));
  }

  updateContact = (index, changes) => {
    this.updateContacts((contacts) => {
      contacts[index] = merge(contacts[index], changes);
    });
  }

  deleteContact = (index) => {
    this.updateContacts((contacts) => {
      if (contacts[index].id) {
        contacts[index] = { ...contacts[index], _destroy: true };
      } else {
        contacts.splice(index, 1);
      }
    });
  };

  updateContacts(block) {
    const contacts = [...this.props.contacts];
    block(contacts);
    this.props.onContactsUpdate(contacts);
  }

  renderContactRow(contact, index) {
    if (contact._destroy) {
      return;
    }

    return (
      <InlineContactRelationForm
        availableDatabaseOptions={this.props.availableDatabaseOptions}
        contact={contact}
        contactRoles={this.props.contactRoles}
        key={index}
        onContactUpdate={changes => this.updateContact(index, changes)}
        onDelete={() => this.deleteContact(index)}
      />
    );
  }

  renderContacts() {
    return this.props.contacts.map((contact, index) => this.renderContactRow(contact, index));
  }

  undoPendingDeletes = (e) => {
    e.preventDefault();
    this.updateContacts((contacts) => {
      contacts.map((contact) => {
        contact._destroy = false;
      });
    });
  };

  renderPendingDeletes() {
    const numPendingDeletes = this.props.contacts.filter(contact => contact._destroy).length;

    if (numPendingDeletes === 0) {
      return;
    }

    return (
      <div className="my-2">
        <span className="mr-3">
          {pluralize(numPendingDeletes, "deleted contact")} will not be removed until the form is saved.
        </span>
        <a href="#" name="undo-pending-deletes" onClick={this.undoPendingDeletes}>Undo</a>
      </div>
    );
  }

  renderAddContactLink() {
    return (
      <div>
        <span>
          <a className="d-inline-block" href="#" name="add-contact-row" onClick={this.addContact}>
            <FontAwesomeIcon className="plus-icon mr-1" icon={faPlus} />
            Add a Contact
          </a>
          { this.listContactRolesToAdd().length > 0 &&
            <a
              className="ml-3 d-inline-block"
              href="#"
              name="add-related-contact-row"
              onClick={this.showContactCopyModal}
            >
              <FontAwesomeIcon className="plus-icon mr-1" icon={faPlus} />
              Property Contacts
            </a>
          }
        </span>
      </div>
    );
  }

  renderCopyContactRoleModal() {
    return (
      <CopyContactRoleModalBase
        copyFromString="Database Property"
        projectContactRoles={this.listContactRolesToAdd()}
        show={this.state.showContactCopyModal}
        onAddContactRoles={this.copyFromContactRoles}
        onClose={this.hideContactCopyModal}
      />
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderCopyContactRoleModal()}
        {this.renderContacts()}
        {this.renderPendingDeletes()}
        {this.renderAddContactLink()}
      </React.Fragment>
    );
  }
}
